import {
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Skeleton,
  Space,
  Spin,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { uniqBy } from "lodash";
import { normalizeDateToLocal, normalizeDateToLocalYear } from "../../utils";
import {
  fetchData,
  fetchLovData,
  fetchLovGridData,
  generateReport,
} from "../../features/fetch/fetchSlice";
import { dynamicTypeData } from "../../data";

function GenerateDynamicModal({ open, handleCancel, prodd }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const url = "api/v1/reports/getColumns";
  const urlFilters = "api/v1/reports/dynamicFilterParam";

  const { lovData, loading, loadingGrid, loadingGenerateReport } = useSelector(
    (state) => state.fetch
  );
  const { data, dataFilters } = useSelector((state) => ({
    data: state.fetch.data[url] || [],
    dataFilters: state.fetch.data[urlFilters] || [],
  }));

  const [loadingOptions, setLoadingOptions] = useState({});
  const [selectOptions, setSelectOptions] = useState({});
  const selectedColumns = Form.useWatch("reportColumns", form);
  const selectedFormat = Form.useWatch("format", form);
  const tooManyParams = selectedColumns?.length > 6;

  useEffect(() => {
    if (tooManyParams && form.getFieldValue("format")?.toUpperCase() === "PDF") {
      form.setFieldsValue({ format: undefined });
    }
  }, [tooManyParams]);

  useEffect(() => {
    if (prodd?.key) {
      dispatch(
        fetchData({
          url: url,
          reportName: prodd?.key,
        })
      );
      dispatch(
        fetchData({
          url: urlFilters,
          reportName: prodd?.key,
        })
      );
    }
  }, [prodd?.key, dispatch]);

  const fetchSelectOptions = async (paramId) => {
    setLoadingOptions((prev) => ({ ...prev, [paramId]: true }));
    const result = await dispatch(
      fetchLovGridData({
        url: "api/v1/reports/fetch-lov-data-grid",
        paramId,
      })
    )?.unwrap();
    const uniqueOptions = uniqBy(result?.data?.result, "v_key_id")?.map(
      (item) => ({
        value: item?.v_key_id,
        label: item?.v_name,
      })
    );

    setSelectOptions((prev) => ({
      ...prev,
      [paramId]: uniqueOptions,
    }));

    setLoadingOptions((prev) => ({ ...prev, [paramId]: false }));
  };

  const onFinish = async (data) => {
    const transformedValues = Object.fromEntries(
      Object.entries(data)?.map(([key, value]) => {
        const param = dataFilters?.find((item) => item?.paramId === key);
        const formattedValue =
          param?.paramType === "DATE" ? normalizeDateToLocalYear(value) : value;
        return [key, formattedValue ?? null];
      })
    );
    const filters = [
      Object.fromEntries(
        dataFilters
          ?.map((item) => item?.paramId)
          ?.filter((key) => key in transformedValues)
          ?.map((key) => [key, transformedValues[key]])
      ),
    ];
    const res = await dispatch(
      generateReport({
        url: "api/v1/reports/generateDynamicReport",
        filters,
        reportName: prodd?.key,
        format: data?.format,
        reportColumns: Array.isArray(data?.reportColumns)
          ? data?.reportColumns.join(",")
          : data?.reportColumns,
      })
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;

      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];

      let fileName = "report";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      await toast.success(`${fileName} downloaded successfully`); 
    } else {
      toast.error("Failed to download file");
    }
  };

  const handleModalCancel = () => {
    form.resetFields();
    handleCancel();
  };

  return (
    <Modal
      className="modal-btn mod-height mod-footer"
      title={`Generate report for ${prodd?.title}`}
      open={open}
      onOk={form.submit}
      onCancel={handleModalCancel}
      width={850}
      maskClosable={false}
    >
      {loading ? (
        <Skeleton />
      ) : (
        <Form
          layout="vertical"
          ref={formRef}
          name="dynamic-form"
          onFinish={onFinish}
          form={form}
          style={{ maxWidth: "100%" }}
        >
          {tooManyParams && (
            <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded mb-4">
              More than 6 parameters selected. Report cannot be downloaded as PDF.
            </div>
          )}
          <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
            <Form.Item
              label={"Select Parameter"}
              name="reportColumns"
              rules={[
                {
                  required: true,
                  message: "Required field",
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                dropdownRender={(menu) => (
                  <>
                    <Space
                      style={{
                        padding: "8px 8px 4px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span className="select-info mt-1">
                        Choose one or more of the following parameters
                      </span>
                      <Checkbox
                        checked={
                          form?.getFieldValue("reportColumns")?.length ===
                            data?.length && data?.length > 0
                        }
                        indeterminate={
                          form?.getFieldValue("reportColumns")?.length > 0 &&
                          form?.getFieldValue("reportColumns")?.length <
                            data?.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allValues = data?.map((item) => item?.id);
                            form?.setFieldsValue({ reportColumns: allValues });
                          } else {
                            form?.setFieldsValue({ reportColumns: [] });
                          }
                        }}
                      >
                        Select All
                      </Checkbox>
                    </Space>
                    <Divider style={{ margin: "8px 0" }} />
                    {menu}
                  </>
                )}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
              >
                {data?.length > 0 &&
                  data?.map((item) => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.column_name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="format"
              className=""
              label={"Format"}
              rules={[
                {
                  required: true,
                  message: "Please select format",
                },
              ]}
            >
              <Select
                className=""
                allowClear
                style={{
                  width: "100%",
                }}
                options={
                  dynamicTypeData?.length > 0 &&
                  dynamicTypeData
                    ?.filter((item) => !tooManyParams || item?.value?.toUpperCase() !== "PDF")
                    ?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                }
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label?.toLocaleLowerCase() ?? "").includes(
                    input?.toLocaleLowerCase()
                  )
                }
              />
            </Form.Item>

            {dataFilters?.map((item) => (
              <div key={item?.paramId}>
                <Form.Item
                  key={item?.paramId}
                  name={item?.paramId}
                  label={item?.paramLabel}
                  rules={[
                    {
                      required: item?.paramRequired === "Y" ? true : false,
                      message: `Please select ${item?.paramLabel}`,
                    },
                  ]}
                >
                  {item?.paramType === "DATE" ? (
                    <DatePicker
                      className="input"
                      placeholder={`Select date`}
                      format={"YYYY MMM DD"}
                      style={{ width: "100%" }}
                    />
                  ) : item?.paramType === "COMBO" ||
                    item?.paramType === "LOV" ? (
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(
                          input.toLowerCase()
                        )
                      }
                      onDropdownVisibleChange={(open) => {
                        if (open && !selectOptions[item?.paramId]) {
                          fetchSelectOptions(item?.paramId);
                        }
                      }}
                      options={selectOptions[item?.paramId] || []}
                      dropdownRender={(menu) => (
                        <>
                          {loadingGrid && (
                            <div
                              style={{
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              <Spin className="sms-spin" />
                            </div>
                          )}
                          {menu}
                        </>
                      )}
                    />
                  ) : item?.paramType === "TEXT" ? (
                    <Input className="input" />
                  ) : item?.paramType === "NUMBER" ? (
                    <InputNumber
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                      className="input flex"
                    />
                  ) : null}
                </Form.Item>
              </div>
            ))}
          </div>
          <div className="flex lg:flex-row flex-col justify-between mt-[56px] mb-[48px]">
            <div className="justify-start"></div>
            <div className="justify-end flex items-center gap-x-2">
              <div className="w-[200px]">
                <button
                  key="back"
                  type="button"
                  onClick={handleModalCancel}
                  className="cstm-btn"
                >
                  Cancel
                </button>
              </div>

              <div className="w-[250px]">
                <button
                  key="submit"
                  type="submit"
                  className="cstm-btn-2"
                  disabled={loadingGenerateReport || (tooManyParams && (!selectedFormat || selectedFormat?.toUpperCase() === "PDF"))}
                >
                  {loadingGenerateReport ? <Spin /> : "   Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
}

export default GenerateDynamicModal;
